import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ui } from "@/lib/ui";
import {
  BarChart3, Rocket, PencilLine, Users, Activity, Loader2, ArrowUpDown,
  Image as ImageIcon, ChevronDown, ChevronRight,
} from "lucide-react";
import { DateRangeFilter } from "@/components/DateRangeFilter";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const ACTION_LABEL = {
  "site.create": "created a website",
  "site.generate.completed": "generated a website",
  "site.deploy": "deployed a site",
  "domain.connect": "connected a domain",
  "gsc.upload": "uploaded a GSC file",
  "site.edit": "edited a site",
  "site.rollback": "rolled back a version",
  "site.delete": "deleted a site",
  "logo.generate": "generated a logo",
  "regenerate.all": "regenerated all sites",
  "image.generate": "generated photos",
  "image.generate_batch": "generated a photo batch",
  "image.variants": "generated photo variants",
  "image.streetview_clean": "cleaned a Street View photo",
};

// Future-proof: any new automation that logs an action gets a readable label
// automatically, so its per-user usage shows up here with zero extra work.
const humanize = (action) =>
  ACTION_LABEL[action] ||
  (action || "")
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

function Metric({ icon: Icon, label, value, tint }) {
  return (
    <div className={`${ui.card} p-5`} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-4.5 h-4.5" /></div>
        <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400 font-semibold">{label}</span>
      </div>
      <div className="text-3xl font-black text-slate-100 font-h tabular-nums">{value}</div>
    </div>
  );
}

export default function ActivityReports() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("websites_generated");
  const [sortDir, setSortDir] = useState("desc");
  const [expanded, setExpanded] = useState(null);
  const [range, setRange] = useState(null);

  useEffect(() => {
    const params = range ? { from: range.from.toISOString(), to: range.to.toISOString() } : {};
    Promise.all([api.get("/activity/summary", { params }), api.get("/activity/recent", { params: { ...params, limit: 40 } })])
      .then(([s, r]) => { setData(s.data); setRecent(r.data.events || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [range]);

  const rows = useMemo(() => {
    const list = [...(data?.users || [])];
    list.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === "last_active") { av = av || ""; bv = bv || ""; }
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    return list;
  }, [data, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const Th = ({ k, children, right }) => (
    <th className={`px-4 py-3 ${right ? "text-right" : "text-left"}`}>
      <button onClick={() => toggleSort(k)} data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 hover:text-slate-200 ${sortKey === k ? "text-indigo-300" : ""}`}>
        {children} <ArrowUpDown className="w-3 h-3 opacity-60" />
      </button>
    </th>
  );

  if (loading) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 spin" /> Loading activity…</div>;

  const t = data?.totals || {};
  return (
    <div className="space-y-6 animate-fade-up" data-testid="activity-reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={ui.pageHeader}>Activity &amp; Reports</h1>
          <p className={ui.muted}>Per-user usage across every OpusOS automation.</p>
        </div>
        <DateRangeFilter testid="activity-date-range" value={range} onChange={setRange} align="right" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Metric icon={BarChart3} label="Websites Generated" value={t.websites_generated ?? 0} tint="bg-indigo-500/15 text-indigo-300" />
        <Metric icon={ImageIcon} label="Photos Generated" value={t.photos_generated ?? 0} tint="bg-fuchsia-500/15 text-fuchsia-300" />
        <Metric icon={Rocket} label="Deploys" value={t.deploys ?? 0} tint="bg-emerald-500/15 text-emerald-300" />
        <Metric icon={PencilLine} label="AI Edits" value={t.edits ?? 0} tint="bg-sky-500/15 text-sky-300" />
        <Metric icon={Users} label="Team Members" value={t.users ?? 0} tint="bg-amber-500/15 text-amber-300" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <h2 className={`${ui.sectionHeader} mb-3`}>Per-user metrics</h2>
          <p className="text-xs text-slate-500 mb-3">Click a row to see a full breakdown of every activity that user has made.</p>
          <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-900/50 backdrop-blur-sm">
            <table className="w-full" data-testid="user-metrics-table">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/60 text-[11px] font-mono uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3 text-left">User</th>
                  <Th k="websites_generated" right>Websites</Th>
                  <Th k="photos_generated" right>Photos</Th>
                  <Th k="deploys" right>Deploys</Th>
                  <Th k="edits" right>Edits</Th>
                  <Th k="last_active">Last active</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const isOpen = expanded === u.email;
                  const acts = Object.entries(u.actions || {}).sort((a, b) => b[1] - a[1]);
                  return (
                    <>
                      <tr key={u.email} onClick={() => setExpanded(isOpen ? null : u.email)}
                        className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors text-sm cursor-pointer"
                        data-testid={`user-row-${u.email}`}>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
                            <div>
                              <div className="text-slate-200 font-medium">{u.name || u.email}</div>
                              <div className="text-xs text-slate-500 font-mono">{u.email}{u.role ? ` · ${u.role}` : ""}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-right tabular-nums text-indigo-300 font-semibold">{u.websites_generated}</td>
                        <td className="px-4 py-3.5 text-right tabular-nums text-fuchsia-300 font-semibold" data-testid={`photos-${u.email}`}>{u.photos_generated ?? 0}</td>
                        <td className="px-4 py-3.5 text-right tabular-nums text-slate-300">{u.deploys}</td>
                        <td className="px-4 py-3.5 text-right tabular-nums text-slate-300">{u.edits}</td>
                        <td className="px-4 py-3.5 text-slate-400 text-xs font-mono">{fmt(u.last_active)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-950/40 border-b border-slate-800/50" data-testid={`user-breakdown-${u.email}`}>
                          <td colSpan={6} className="px-6 py-4">
                            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-3">Full activity breakdown</div>
                            {acts.length === 0 ? (
                              <div className="text-sm text-slate-500">No recorded activity yet.</div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                                {acts.map(([action, count]) => (
                                  <div key={action} className="flex items-center justify-between text-sm border-b border-slate-800/40 pb-1.5">
                                    <span className="text-slate-300 capitalize">{humanize(action)}</span>
                                    <span className="tabular-nums text-slate-100 font-semibold">{count}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No activity recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 className={`${ui.sectionHeader} mb-3`}>Recent activity</h2>
          <div className={`${ui.card} p-2 max-h-[520px] overflow-y-auto`} data-testid="recent-activity-feed">
            {recent.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">No events yet.</div>}
            {recent.map((e) => (
              <div key={e.id} className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-800/40 transition-colors">
                <div className="w-7 h-7 rounded-md bg-slate-800 flex items-center justify-center shrink-0 mt-0.5">
                  <Activity className="w-3.5 h-3.5 text-indigo-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-slate-300">
                    <span className="text-slate-100 font-medium">{e.user_name || e.user_email || "Someone"}</span>{" "}
                    {ACTION_LABEL[e.action] || humanize(e.action)}
                    {e.detail?.name ? <span className="text-slate-400"> — {e.detail.name}</span> : null}
                    {e.detail?.count ? <span className="text-slate-500"> ({e.detail.count})</span> : null}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">{fmt(e.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
